/**
 * Theme adapter for Virgin Redemption AU site — injects real next/font families.
 */
import type { Theme } from '@emotion/react';
import { buildTheme } from 'common-ui';
import type { CommonThemeVars, ThemeCreateFunction } from 'tsconfig/types.d';
import { montserratFont, gothamFont, robotoFont } from '../common/nextFont';
import {
  buildThemeVarsAPACVws as buildThemeVarsAPACVwsFoundation,
  buildThemeMixinsAPACVws as buildThemeMixinsAPACVwsFoundation,
  buildThemeWidgetStylesAPACVws as buildThemeWidgetStylesAPACVwsFoundation,
  buildThemeWidgetStylesAPACVwsFromBase,
  buildThemeWidgetStylesAPACLWFromBase,
  buildCoreWidgets,
  buildProductCardWidgets,
  buildProductWidgets,
  buildSearchWidgets,
  buildMiniCartWidgets,
  buildCartWidgets,
  buildAccountWidgets,
  buildLandingpageWidgets,
} from './themeFoundation';

export const buildThemeVarsAPACVws = (
  vars: Pick<Theme, CommonThemeVars>,
): ReturnType<typeof buildThemeVarsAPACVwsFoundation> =>
  buildThemeVarsAPACVwsFoundation(vars, {
    montserrat: montserratFont.style.fontFamily,
    gotham: gothamFont.style.fontFamily,
    roboto: robotoFont.style.fontFamily,
  });

export const buildThemeMixinsAPACVws = (
  vars: Omit<Theme, 'mixins' | 'widgets'>,
): ReturnType<typeof buildThemeMixinsAPACVwsFoundation> => buildThemeMixinsAPACVwsFoundation(vars);

export const buildThemeWidgetStylesAPACVws = (
  vars: Omit<Theme, 'widgets'>,
): ReturnType<typeof buildThemeWidgetStylesAPACVwsFoundation> => buildThemeWidgetStylesAPACVwsFoundation(vars);

// CMS pages: core + product cards + minicart (header)
export const createCmsPageTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsAPACVws, buildThemeMixinsAPACVws, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesAPACVwsFromBase(
      vars,
      buildThemeWidgetStylesAPACLWFromBase(vars, {
        ...buildCoreWidgets(vars),
        ...buildProductCardWidgets(vars),
        ...buildMiniCartWidgets(vars),
      }),
    ),
  );

// PDP: core + full product + minicart (header)
export const createProductTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsAPACVws, buildThemeMixinsAPACVws, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesAPACVwsFromBase(
      vars,
      buildThemeWidgetStylesAPACLWFromBase(vars, {
        ...buildCoreWidgets(vars),
        ...buildProductWidgets(vars),
        ...buildMiniCartWidgets(vars),
      }),
    ),
  );

// Search/PLP: core + search + product cards + minicart
export const createSearchTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsAPACVws, buildThemeMixinsAPACVws, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesAPACVwsFromBase(
      vars,
      buildThemeWidgetStylesAPACLWFromBase(vars, {
        ...buildCoreWidgets(vars),
        ...buildSearchWidgets(vars),
        ...buildProductWidgets(vars),
        ...buildMiniCartWidgets(vars),
      }),
    ),
  );

// Cart: core + cart + minicart
export const createCartTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsAPACVws, buildThemeMixinsAPACVws, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesAPACVwsFromBase(
      vars,
      buildThemeWidgetStylesAPACLWFromBase(vars, {
        ...buildCoreWidgets(vars),
        ...buildCartWidgets(vars),
      }),
    ),
  );

// Account: core + account + minicart (header)
export const createAccountTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsAPACVws, buildThemeMixinsAPACVws, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesAPACVwsFromBase(
      vars,
      buildThemeWidgetStylesAPACLWFromBase(vars, {
        ...buildCoreWidgets(vars),
        ...buildAccountWidgets(vars),
        ...buildMiniCartWidgets(vars),
      }),
    ),
  );

// Landing/offer pages: core + landing page + minicart
export const createLandingPageTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsAPACVws, buildThemeMixinsAPACVws, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesAPACVwsFromBase(
      vars,
      buildThemeWidgetStylesAPACLWFromBase(vars, {
        ...buildCoreWidgets(vars),
        ...buildLandingpageWidgets(vars),
        ...buildMiniCartWidgets(vars),
      }),
    ),
  );

const createTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsAPACVws, buildThemeMixinsAPACVws, buildThemeWidgetStylesAPACVws);
export default createTheme;
